import { useEffect, useState } from 'react';
import styles from './FriendsPage.module.scss';
import FriendProfileCard from '@/components/FriendProfile/FriendProfileCard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { claimRef, getRef } from '@/api/modules/Ref/refApi';
import { useNotificationStore } from '@/store/NotificationStore';
import { Loader } from '@/components/Loader/Loader';
import FriendsHeader from './FriendsHeader/FriendsHeader';
import { ReactComponent as StarIcon } from '@/assets/Icons/star.svg';

type Reward = {
  amount: number;
  link_user: string;
  name: string;
  photo_url: string;
  user_id: number;
};

import WebApp from '@twa-dev/sdk';

export default function FriendPage() {
  const tg = window?.Telegram?.WebApp;
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ref-data'],
    queryFn: getRef,
  });
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );

  const [isClaiming, setIsClaiming] = useState(false);

  // safe-area inset для iOS
  useEffect(() => {
    const tg = (window as any)?.Telegram?.WebApp;
    if (!tg) return;

    const apply = () => {
      const top = tg?.contentSafeAreaInset?.top ?? 0;
      document.documentElement.style.setProperty(
        '--tg-viewport-content-safe-area-inset-top',
        `${top}px`,
      );
    };

    apply();
    tg.onEvent?.('contentSafeAreaChanged', apply);
    return () => tg.offEvent?.('contentSafeAreaChanged', apply);
  }, []);

  // запрет скролла если контента меньше 100vh
  // Временно закомментировано из-за проблем на iPhone
  // useEffect(() => {
  //   const checkScroll = () => {
  //     const body = document.body;
  //     const html = document.documentElement;
  //     const pageHeight = Math.max(
  //       body.scrollHeight,
  //       body.offsetHeight,
  //       html.clientHeight,
  //       html.scrollHeight,
  //       html.offsetHeight,
  //     );

  //     if (pageHeight <= window.innerHeight) {
  //       document.body.style.overflow = 'hidden';
  //     } else {
  //       document.body.style.overflow = '';
  //     }
  //   };

  //   checkScroll();
  //   window.addEventListener('resize', checkScroll);

  //   return () => {
  //     window.removeEventListener('resize', checkScroll);
  //     document.body.style.overflow = '';
  //   };
  // }, []);

  const fallbackCopyText = (text: string) => {
    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    setNotification({
      type: 'Success',
      message: 'Ссылка скопирована',
    });
  };

  const handleCopy = () => {
    if (WebApp && (WebApp as any).HapticFeedback) {
      (WebApp as any).HapticFeedback.notificationOccurred('success');
    }
    if (!data?.ref_link) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(data.ref_link)
        .then(() => {
          setNotification({
            type: 'Success',
            message: 'Ссылка скопирована',
          });
        })
        .catch(() => {
          fallbackCopyText(data.ref_link);
        });
    } else {
      fallbackCopyText(data.ref_link);
    }
  };

  const handleClaim = async () => {
    if (isClaiming) return;

    setIsClaiming(true);
    try {
      await claimRef();

      queryClient.invalidateQueries({ queryKey: ['ref-data'] });

      setNotification({
        type: 'Success',
        message: 'Награда успешно собрана!',
      });
    } catch (error) {
      setNotification({
        type: 'Error',
        message: 'Ошибка при сборе награды',
      });
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div
      className={`${styles.container} ${isLoading ? styles.container_loading : ''}`}
    >
      <FriendsHeader isLoading={isLoading} />

      {isLoading ? (
        <div className={styles.container__loaderContainer}>
          <Loader />
        </div>
      ) : (
        <div className={styles.container__contentContainer}>
          <div className={styles.container__content}>
            <div className={styles.totalInfo}>
              <div className={styles.totalInfoItem}>
                <div className={styles.totalInfoItemTitle}>
                  Получено за все время
                </div>
                <div className={styles.totalInfoItemValue}>
                  <div className={styles.totalInfoItemValueAmount}>
                    {data.all_amount}
                  </div>
                  <div className={styles.totalInfoItemValueStar}>
                    <StarIcon />
                  </div>
                </div>
              </div>
              <div className={styles.totalInfoItem}>
                <div className={styles.totalInfoItemTitle}>Текущий баланс</div>
                <div className={styles.totalInfoItemValue}>
                  <div className={styles.totalInfoItemValueAmount}>
                    {data.sum_amount}
                  </div>
                  <div className={styles.totalInfoItemValueStar}>
                    <StarIcon />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <button
                className={styles.claimButton}
                disabled={data.sum_amount === 0 || isClaiming}
                onClick={handleClaim}
              >
                {isClaiming ? 'Собираем...' : 'Собрать награду'}
              </button>
            </div>
            <div className={styles.friendsCount}>
              <span className={styles.friends__heading__text}>Друзья</span>
              <span
                style={{ color: '#818392' }}
                className={styles.friends__heading__count}
              >
                ({data.rewards.length})
              </span>
            </div>
            <div className={styles.friend__list}>
              {data.rewards.map((reward: Reward) => (
                <>
                  <FriendProfileCard
                    key={reward.user_id}
                    name={reward.name}
                    rewardNumber={reward.amount}
                    photoUrl={reward.photo_url}
                  />
                </>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* подложка от самого низа */}
      {!isLoading && <div className={styles.referalBackdrop} aria-hidden />}

      {/* сами кнопки */}
      {!isLoading && (
        <div className={styles.referal}>
          <button
            onClick={() => {
              if (WebApp && (WebApp as any).HapticFeedback) {
                (WebApp as any).HapticFeedback.notificationOccurred('success');
              }
              const link = `https://t.me/share/url?url=${encodeURIComponent(
                data?.ref_link ?? '',
              )}&text=${encodeURIComponent('')}`;
              tg?.openTelegramLink(link);
            }}
            className={styles.referal__button}
          >
            Пригласить друга
          </button>

          <div
            onClick={handleCopy}
            style={{ cursor: 'pointer' }}
            title="Скопировать ссылку"
          >
            <svg
              width="52"
              height="52"
              viewBox="0 0 44 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="43"
                height="43"
                rx="11.5"
                stroke="#FF9E0E"
              />
              <path
                d="M28.375 31.75H19.625C17.448 31.75 16.25 30.552 16.25 28.375V19.625C16.25 17.448 17.448 16.25 19.625 16.25H28.375C30.552 16.25 31.75 17.448 31.75 19.625V28.375C31.75 30.552 30.552 31.75 28.375 31.75ZM19.625 17.75C18.293 17.75 17.75 18.293 17.75 19.625V28.375C17.75 29.707 18.293 30.25 19.625 30.25H28.375C29.707 30.25 30.25 29.707 30.25 28.375V19.625C30.25 18.293 29.707 17.75 28.375 17.75H19.625ZM13.75 25V15.625C13.75 14.293 14.293 13.75 15.625 13.75H25C25.414 13.75 25.75 13.414 25.75 13C25.75 12.586 25.414 12.25 25 12.25H15.625C13.448 12.25 12.25 13.448 12.25 15.625V25C12.25 25.414 12.586 25.75 13 25.75C13.414 25.75 13.75 25.414 13.75 25Z"
                fill="#FFAF39"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
